import {IQuoteStockItem} from './quoteStockItems';
import {
  IQuoteComposeEntry,
  IQuoteSectionEntry,
  QUOTE_GRID_SECTION_CODE_PREFIX,
} from './quote-compose-entry.interface';

export const QUOTE_GRID_ROW_KIND_BLANK = 'blank';

export const QUOTE_GRID_ROW_KIND_SECTION = 'section';

export const QUOTE_GRID_ROW_KIND_ITEM = 'item';

/**
 * AG Grid row for quote compose — blank search row, section header, or stock item.
 */
export interface IQuoteGridRow extends IQuoteStockItem {
  quoteRowKind: typeof QUOTE_GRID_ROW_KIND_BLANK | typeof QUOTE_GRID_ROW_KIND_SECTION | typeof QUOTE_GRID_ROW_KIND_ITEM;
  sectionId?: string;
  sectionName?: string;
}

/**
 * Returns true when the grid row is a section header.
 */
export const isQuoteSectionGridRow = (
  row: IQuoteGridRow | IQuoteStockItem | null | undefined,
): row is IQuoteGridRow & { quoteRowKind: typeof QUOTE_GRID_ROW_KIND_SECTION; sectionId: string } => {
  return (row as IQuoteGridRow | undefined)?.quoteRowKind === QUOTE_GRID_ROW_KIND_SECTION;
};

/**
 * Returns true when the grid row is the blank stock-search row.
 */
export const isQuoteBlankGridRow = (
  row: IQuoteGridRow | IQuoteStockItem | null | undefined,
): row is IQuoteGridRow & { quoteRowKind: typeof QUOTE_GRID_ROW_KIND_BLANK } => {
  return (row as IQuoteGridRow | undefined)?.quoteRowKind === QUOTE_GRID_ROW_KIND_BLANK;
};

/**
 * Returns true when the grid row is a stock item line.
 */
export const isQuoteItemGridRow = (
  row: IQuoteGridRow | IQuoteStockItem | null | undefined,
): row is IQuoteGridRow & { quoteRowKind: typeof QUOTE_GRID_ROW_KIND_ITEM; code: string } => {
  return (row as IQuoteGridRow | undefined)?.quoteRowKind === QUOTE_GRID_ROW_KIND_ITEM;
};

/**
 * Maps compose entries to grid rows (without the blank search row).
 */
export const mapQuoteComposeEntriesToGridRows = (entries: IQuoteComposeEntry[]): IQuoteGridRow[] => {
  return entries.map((entry: IQuoteComposeEntry): IQuoteGridRow => {
    if (entry.kind === 'section') {
      return mapQuoteSectionEntryToGridRow(entry);
    }

    return {
      ...entry.item,
      quoteRowKind: QUOTE_GRID_ROW_KIND_ITEM,
    };
  });
};

/**
 * Maps a section entry to a grid row.
 */
export const mapQuoteSectionEntryToGridRow = (entry: IQuoteSectionEntry): IQuoteGridRow => ({
  quoteRowKind: QUOTE_GRID_ROW_KIND_SECTION,
  sectionId: entry.id,
  sectionName: entry.name,
  code: `${QUOTE_GRID_SECTION_CODE_PREFIX}${entry.id}`,
  desc: entry.name,
  avCost: null,
  barcode: null,
  binL: null,
  category: null,
  created: null,
  dep: null,
  discExempt: null,
  dlvColl: null,
  excludeSellingValue: null,
  genCode: null,
  gp1: null,
  lastMoved: null,
  lastPurchase: null,
  lastSold: null,
  lastStockTake: null,
  latestCost: null,
  lineColourCode: null,
  maxDisc: null,
  modified: null,
  noDecimal: null,
  onHand: null,
  onHoldCode: null,
  ordLvl: null,
  packSize: null,
  prvSellingPri: null,
  prvSellingPriDate: null,
  purchaseOrder: null,
  recommendedGP: null,
  regularSuppCode: null,
  reportItemCode: null,
  reportItemFactor: null,
  salesOrder: null,
  sellIntoNegative: null,
  sellPriExcl1: null,
  sellPriIncl1: null,
  sellUnderCost: null,
  subDep: null,
  suppCode: null,
  suppUsedLast1: null,
  suppUsedLast2: null,
  suppUsedLast3: null,
  suppUsedLast4: null,
  suppUsedLast5: null,
  suppUsedLastDate1: null,
  suppUsedLastDate2: null,
  suppUsedLastDate3: null,
  suppUsedLastDate4: null,
  suppUsedLastDate5: null,
  suppUsedLastPrice1: null,
  suppUsedLastPrice2: null,
  suppUsedLastPrice3: null,
  suppUsedLastPrice4: null,
  suppUsedLastPrice5: null,
  unitsYear: null,
  user: null,
  vatR: null,
  web: null,
  orderQty: null,
  total: null,
  discount: null,
  originalSellPriIncl1: null,
  nominalGP: null,
  itemDiscount: null,
});
